import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Star } from 'lucide-react';
import { AvailableSubscriptionResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import SubscriptionForm from '../components/SubscriptionForm';
import { useAuth } from '../contexts/AuthContext';

const Subscriptions: React.FC = () => {
  const { canManage } = useAuth();
  const [subscriptions, setSubscriptions] = useState<AvailableSubscriptionResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    isShow: '' as '' | 'true' | 'false'
  });
  const [showForm, setShowForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<AvailableSubscriptionResource | null>(null);

  useEffect(() => {
    loadSubscriptions();
  }, [page, perPage, search, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      // Convert empty strings to undefined for API
      const apiFilters = {
        isShow: filters.isShow === '' ? undefined : filters.isShow === 'true' ? true : false,
      };
      const response = await api.getSubscriptions(page, perPage, search, apiFilters);
      setSubscriptions(response.data);
      setTotal(response.meta.total);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      // Mock data for development
      setSubscriptions([
        {
          id: '1',
          name: 'Basic Plan',
          description: 'Basic subscription plan for small businesses',
          maxBranch: 1,
          fnbMode: true,
          maxTransactionPerMonth: 1000,
          maxTransactionPerDay: 50,
          months: 1,
          price: 100000,
          annuallyDiscountPercent: 10,
          themeColor: '#3B82F6',
          isPopular: false,
          isShow: true,
          level: 1,
          externalSubscribeLink: undefined,
          benefits: ['Basic features', 'Email support', '1 branch'],
          appstoreProductId: undefined,
          appstoreAnnualProductId: undefined,
          createdAt: '2024-01-01T00:00:00.000000Z',
          updatedAt: '2024-01-01T00:00:00.000000Z'
        },
        {
          id: '2',
          name: 'Business Plan',
          description: 'Business subscription plan for growing companies',
          maxBranch: 5,
          fnbMode: true,
          maxTransactionPerMonth: 5000,
          maxTransactionPerDay: 200,
          months: 1,
          price: 250000,
          annuallyDiscountPercent: 15,
          themeColor: '#10B981',
          isPopular: true,
          isShow: true,
          level: 2,
          externalSubscribeLink: undefined,
          benefits: ['Advanced features', 'Priority support', '5 branches', 'Analytics'],
          appstoreProductId: undefined,
          appstoreAnnualProductId: undefined,
          createdAt: '2024-01-01T00:00:00.000000Z',
          updatedAt: '2024-01-01T00:00:00.000000Z'
        }
      ]);
      setTotal(2);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleFiltersClear = () => {
    setFilters({
      isShow: ''
    });
    setPage(1);
  };

  const handleCreate = () => {
    setEditingSubscription(null);
    setShowForm(true);
  };

  const handleEdit = (subscription: AvailableSubscriptionResource) => {
    setEditingSubscription(subscription);
    setShowForm(true);
  };

  const handleDelete = async (subscriptionId: string) => {
    if (window.confirm('Are you sure you want to delete this subscription?')) {
      try {
        await api.deleteSubscription(subscriptionId);
        loadSubscriptions();
      } catch (error) {
        console.error('Error deleting subscription:', error);
        alert('Failed to delete subscription');
      }
    }
  };

  const handleFormSubmit = async () => {
    setShowForm(false);
    setEditingSubscription(null);
    loadSubscriptions();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLevelColor = (level: number) => {
    if (level >= 3) return 'bg-purple-100 text-purple-800';
    if (level >= 2) return 'bg-blue-100 text-blue-800';
    if (level >= 1) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getLevelLabel = (level: number) => {
    if (level >= 3) return 'Premium';
    if (level >= 2) return 'Business';
    if (level >= 1) return 'Basic';
    return 'Trial';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscriptions</h1>
            <p className="text-gray-600">Manage subscription plans and pricing</p>
          </div>
          {canManage && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Subscription
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search subscriptions..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Show/Hide Filter */}
          <div>
            <select
              value={filters.isShow}
              onChange={(e) => handleFiltersChange({ ...filters, isShow: e.target.value as '' | 'true' | 'false' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Visibility</option>
              <option value="true">Show</option>
              <option value="false">Hide</option>
            </select>
          </div>
        </div>

        {/* Active Filters */}
        {filters.isShow && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                Visibility: {filters.isShow === 'true' ? 'Show' : 'Hide'}
                <button
                  onClick={() => handleFiltersChange({ ...filters, isShow: '' })}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
              <button
                onClick={handleFiltersClear}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Subscriptions List */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Loading subscriptions..." />
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <Star className="h-12 w-12" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {search || filters.isShow
              ? 'Try adjusting your search or filters.'
              : 'No subscriptions have been created yet.'}
          </p>
          {canManage && (
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Subscription
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                    Features
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start max-w-xs">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div 
                            className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: subscription.themeColor || '#6B7280' }}
                          >
                            {subscription.name.charAt(0)}
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="flex items-start">
                            <div className="text-sm font-medium text-gray-900 break-words">
                              {subscription.name}
                            </div>
                            {subscription.isPopular && (
                              <Star className="h-4 w-4 text-yellow-500 ml-2 flex-shrink-0 mt-0.5" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500 break-words mt-1">{subscription.description}</div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelColor(subscription.level)} mt-2`}>
                            {getLevelLabel(subscription.level)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div>Branches: {subscription.maxBranch}</div>
                        <div>Monthly: {subscription.maxTransactionPerMonth === 0 ? 'Unlimited' : subscription.maxTransactionPerMonth.toLocaleString()}</div>
                        <div>F&B: {subscription.fnbMode ? 'Yes' : 'No'}</div>
                        <div>Duration: {subscription.months} month{subscription.months > 1 ? 's' : ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(subscription.price)}
                      </div>
                      {subscription.annuallyDiscountPercent && (
                        <div className="text-sm text-green-600">
                          {subscription.annuallyDiscountPercent}% off annually
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {subscription.isShow ? (
                          <Eye className="h-4 w-4 text-green-600" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-900">
                          {subscription.isShow ? 'Visible' : 'Hidden'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(subscription.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {canManage && (
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(subscription)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(subscription.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && subscriptions.length > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            total={total}
            perPage={perPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Subscription Form Modal */}
      {showForm && (
        <SubscriptionForm
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingSubscription(null);
          }}
          subscription={editingSubscription}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default Subscriptions;
